import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import ClientPicker from './ClientPicker';
import Icon from './Icon';
import { fetchNbsRate } from '../lib/nbs';
import { todayStr, formatRSD, formatCur } from '../lib/helpers';

// Najčešće valute za inostrane fakture (kurs NBS se uzima automatski pri čuvanju)
const POPULAR_CURRENCIES = [
  ['EUR', 'Evro', 'Euro', 'Евро'],
  ['USD', 'Američki dolar', 'US dollar', 'Доллар США'],
  ['CHF', 'Švajcarski franak', 'Swiss franc', 'Швейцарский франк'],
  ['GBP', 'Britanska funta', 'British pound', 'Фунт стерлингов'],
  ['BAM', 'Konvertibilna marka (BiH)', 'Convertible mark (BiH)', 'Конвертируемая марка (БиГ)'],
  ['MKD', 'Makedonski denar', 'Macedonian denar', 'Македонский денар'],
  ['HUF', 'Mađarska forinta', 'Hungarian forint', 'Венгерский форинт'],
  ['CZK', 'Češka kruna', 'Czech koruna', 'Чешская крона'],
  ['PLN', 'Poljski zlot', 'Polish zloty', 'Польский злотый'],
  ['SEK', 'Švedska kruna', 'Swedish krona', 'Шведская крона'],
  ['NOK', 'Norveška kruna', 'Norwegian krone', 'Норвежская крона'],
  ['DKK', 'Danska kruna', 'Danish krone', 'Датская крона'],
  ['CAD', 'Kanadski dolar', 'Canadian dollar', 'Канадский доллар'],
  ['AUD', 'Australijski dolar', 'Australian dollar', 'Австралийский доллар'],
  ['JPY', 'Japanski jen', 'Japanese yen', 'Японская иена'],
  ['CNY', 'Kineski juan', 'Chinese yuan', 'Китайский юань'],
  ['RUB', 'Ruska rublja', 'Russian ruble', 'Российский рубль'],
];

// ============================================================
// FORMA ZA FAKTURU — raspored kao dokument: izdavalac i datumi gore,
// klijent i "Od" u sredini, stavke, poruka i obračun dole.
// ============================================================

// Meni jedinica: prvo "prazno", pa "upiši sami" (otvara polje za svoju jedinicu), pa Dan, Usluga...
const UNITS = ['Dan', 'Usluga', 'Komad', 'Sat', 'Mesec', 'Paušal', 'm', 'm2', 'm3', 'kg', 'l', 'Kilometar (km)'];
const CUSTOM = '__custom';
const emptyItem = () => ({ name: '', unit: '', quantity: '1', price: '', discount: '0' });
const n = (v) => {
  const x = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(x) ? x : 0;
};
const round2 = (x) => Math.round(x * 100) / 100;
const lineGross = (it) => n(it.quantity) * n(it.price);
const lineDiscount = (it) => lineGross(it) * Math.min(Math.max(n(it.discount), 0), 100) / 100;
const lineTotal = (it) => lineGross(it) - lineDiscount(it);

// Stara faktura (bez stavki) postaje jedna stavka: opis + iznos
const itemsFromInvoice = (inv) => {
  if (Array.isArray(inv.items) && inv.items.length > 0) {
    return inv.items.map((it) => ({
      name: it.name || '', unit: it.unit || '',
      customUnit: Boolean(it.unit) && !UNITS.includes(it.unit),
      quantity: String(it.quantity ?? 1), price: String(it.price ?? ''), discount: String(it.discount ?? 0),
    }));
  }
  return [{ name: inv.description || '', unit: 'Usluga', quantity: '1', price: inv.amount != null ? String(inv.amount) : '', discount: '0' }];
};

export default function InvoiceForm({ forceKind }) {
  const { id } = useParams();
  const isEdit = Boolean(id); // /fakturisanje/:id/izmeni
  const [searchParams] = useSearchParams();
  const prefillClientId = !isEdit ? searchParams.get('klijent') : null; // nova faktura sa stranice klijenta
  const copyFromId = !isEdit ? searchParams.get('kopija') : null;      // "Napravi kopiju" postojeće fakture
  const fromProformaId = !isEdit ? searchParams.get('iz-profakture') : null; // "Napravi fakturu" od profakture
  const { activeAgencyId, profile, refreshYears } = useAgency();
  const { t, tr } = useI18n();
  const navigate = useNavigate();

  const DOC_KINDS = [
    { key: 'faktura', label: t('docKindInvoice'), icon: 'receipt' },
    { key: 'profaktura', label: t('docKindProforma'), icon: 'fileText' },
    { key: 'fiskalni', label: t('docKindFiscal'), icon: 'calculator' },
  ];

  const [documentKind, setDocumentKind] = useState(forceKind || 'faktura');
  const [type, setType] = useState('domaca');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(isEdit); // ručno izmenjen broj se više ne predlaže
  const [customer, setCustomer] = useState(null);
  const [originalClient, setOriginalClient] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [currency, setCurrency] = useState('EUR');
  const [docLang, setDocLang] = useState('sr'); // jezik inostrane fakture: sr · en · ru
  const [exchangeRate, setExchangeRate] = useState('');
  const [nbsDate, setNbsDate] = useState('');
  const [issueDate, setIssueDate] = useState(todayStr());
  const [serviceDate, setServiceDate] = useState(todayStr());
  const [hasDue, setHasDue] = useState(false); // datum valute je opcion
  const [dueDate, setDueDate] = useState(todayStr());
  const [dueTouched, setDueTouched] = useState(false);
  const [placeOfIssue, setPlaceOfIssue] = useState(profile?.city || '');
  const [note, setNote] = useState('');
  const [issuerNameMode, setIssuerNameMode] = useState(profile?.name_display === 'short' ? 'short' : 'full');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(isEdit || Boolean(prefillClientId) || Boolean(copyFromId) || Boolean(fromProformaId));
  // Faktura iz profakture: { id, number } profakture i već postojeća (nestornirana) faktura od nje, ako je ima
  const [sourceProforma, setSourceProforma] = useState(null);
  const [alreadyInvoiced, setAlreadyInvoiced] = useState(null);

  // Popunjavanje forme iz postojećeg dokumenta (izmena ili kopija)
  const fillFrom = async (inv, { asCopy }) => {
    if (!forceKind || !asCopy) setDocumentKind(inv.document_kind || 'faktura');
    setType(inv.type || 'domaca');
    setItems(itemsFromInvoice(inv));
    if (inv.type === 'inostrana') {
      setCurrency(inv.currency || 'EUR');
      setDocLang(['en', 'ru'].includes(inv.doc_lang) ? inv.doc_lang : 'sr');
      if (!asCopy) setExchangeRate(inv.exchange_rate != null ? String(inv.exchange_rate) : '');
    }
    setPlaceOfIssue(inv.place_of_issue || '');
    setNote(inv.note || '');
    setHasDue(Boolean(inv.due_date));
    if (inv.issuer_name_mode) setIssuerNameMode(inv.issuer_name_mode);
    setOriginalClient(inv.client || '');
    if (!asCopy) {
      setInvoiceNumber(inv.invoice_number || '');
      setNbsDate(inv.nbs_rate_date || '');
      setIssueDate(inv.issue_date || todayStr());
      setServiceDate(inv.service_date || todayStr());
      setDueDate(inv.due_date || inv.issue_date || todayStr());
      setDueTouched(true);
    }
    if (inv.customer_id) {
      const { data: c } = await supabase.from('customers').select('*').eq('id', inv.customer_id).maybeSingle();
      setCustomer(c || null);
    }
  };

  useEffect(() => {
    const sourceId = isEdit ? id : (copyFromId || fromProformaId);
    if (!sourceId) return;
    (async () => {
      const { data: inv, error } = await supabase.from('invoices').select('*').eq('id', sourceId).maybeSingle();
      if (error) setSaveError(error.message);
      if (inv) await fillFrom(inv, { asCopy: !isEdit });
      // Od profakture uvek nastaje faktura (novi broj, današnji datum i kurs) i pamti se veza
      if (inv && fromProformaId && inv.document_kind === 'profaktura' && inv.agency_id === activeAgencyId) {
        setDocumentKind('faktura');
        setSourceProforma({ id: inv.id, number: inv.invoice_number });
        const { data: existing } = await supabase
          .from('invoices').select('id, invoice_number')
          .eq('source_proforma_id', inv.id).or('status.is.null,status.neq.stornirano')
          .limit(1).maybeSingle();
        setAlreadyInvoiced(existing || null);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, copyFromId, fromProformaId]);

  // Nova faktura otvorena sa stranice klijenta: klijent je već izabran
  useEffect(() => {
    if (!prefillClientId) return;
    (async () => {
      const { data: c } = await supabase.from('customers').select('*').eq('id', prefillClientId).maybeSingle();
      if (c) setCustomer(c);
      setLoading(false);
    })();
  }, [prefillClientId]);

  // Datum valute prati datum fakture dok ga korisnik ručno ne promeni
  useEffect(() => { if (!dueTouched) setDueDate(issueDate); }, [issueDate, dueTouched]);

  useEffect(() => { if (documentKind === 'fiskalni') setType('domaca'); }, [documentKind]);

  // Predlog sledećeg broja: najveći broj iste vrste dokumenta u godini izdavanja + 1 (npr. 03/2026)
  useEffect(() => {
    if (isEdit || numberTouched || !activeAgencyId || !issueDate) return;
    let cancelled = false;
    (async () => {
      const y = issueDate.slice(0, 4);
      const { data } = await supabase
        .from('invoices').select('invoice_number')
        .eq('agency_id', activeAgencyId).eq('document_kind', documentKind)
        .gte('issue_date', `${y}-01-01`).lte('issue_date', `${y}-12-31`);
      if (cancelled) return;
      const rows = data || [];
      let max = 0;
      rows.forEach((r) => {
        const num = String(r.invoice_number || '').trim();
        let m = num.match(/^(\d+)\s*[/.-]\s*(\d{4})$/); // 03/2026
        if (m && m[2] === y) { max = Math.max(max, parseInt(m[1], 10)); return; }
        m = num.match(/^(\d{4})\s*[/.-]\s*(\d+)$/); // 2026-003
        if (m && m[1] === y) max = Math.max(max, parseInt(m[2], 10));
      });
      const next = max > 0 ? max + 1 : rows.length + 1;
      setInvoiceNumber(`${String(next).padStart(2, '0')}/${y}`);
    })();
    return () => { cancelled = true; };
  }, [isEdit, numberTouched, activeAgencyId, documentKind, issueDate]);

  // ---------- obračun ----------
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + lineGross(it), 0);
    const discount = items.reduce((s, it) => s + lineDiscount(it), 0);
    return { subtotal: round2(subtotal), discount: round2(discount), total: round2(subtotal - discount) };
  }, [items]);

  const isRsd = type === 'domaca' || documentKind === 'fiskalni';
  const cur = isRsd ? 'RSD' : currency;
  const money = (v) => (isRsd ? formatRSD(v) : formatCur(v, cur));

  const p = profile || {};
  const issuerName = issuerNameMode === 'short' && p.short_name ? p.short_name : p.company_name;
  const issuerAddress = [[p.street, p.street_number].filter(Boolean).join(' '), [p.postal_code, p.city].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const account = isRsd ? p.account_dom : p.account_ino;

  const setItem = (i, key) => (e) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, [key]: e.target.value } : it)));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (i) => setItems((prev) => (prev.length === 1 ? [emptyItem()] : prev.filter((_, j) => j !== i)));

  // Srednji kurs NBS na datum fakture — uzima se automatski pri čuvanju, korisnik ga ne unosi.
  // Potreban je samo za KPO knjigu i limite (preračun u dinare); na fakturi se ne prikazuje.
  const getRateForSave = async () => {
    const today = todayStr();
    try {
      const { rate, date } = await fetchNbsRate(currency, issueDate && issueDate < today ? issueDate : undefined);
      if (Number(rate) > 0) return { rate: Number(rate), date };
    } catch { /* ispod: rezervni kurs */ }
    // Ako NBS trenutno ne odgovara, a faktura već ima kurs (izmena), zadržava se postojeći
    if (n(exchangeRate) > 0) return { rate: n(exchangeRate), date: nbsDate || null };
    return null;
  };

  const cleanItems = items
    .filter((it) => it.name.trim() || n(it.price) > 0)
    .map((it) => ({
      name: it.name.trim(), unit: (it.unit || '').trim(),
      quantity: n(it.quantity), price: n(it.price), discount: Math.min(Math.max(n(it.discount), 0), 100),
    }));
  const itemsOk = cleanItems.length > 0 && cleanItems.every((it) => it.name && it.quantity > 0);
  // Faktura i profaktura moraju imati klijenta (fiskalni račun može i bez, npr. za fizička lica)
  const needsClient = documentKind !== 'fiskalni';
  const hasClient = Boolean(customer) || (isEdit && Boolean(originalClient));
  const valid = itemsOk && totals.total > 0 && issueDate && serviceDate
    && (!needsClient || hasClient) && !alreadyInvoiced;

  const problems = [];
  if (needsClient && !hasClient) problems.push(tr('izaberite klijenta (ili ga dodajte preko "Novi klijent")'));
  if (!cleanItems.length) problems.push(tr('dodajte bar jednu stavku'));
  else if (!itemsOk) problems.push(tr('svaka stavka mora imati naziv i količinu'));
  if (cleanItems.length && totals.total <= 0) problems.push(tr('ukupan iznos mora biti veći od nule'));

  const handleSave = async () => {
    if (!valid) return;
    setSaveError('');
    setSaving(true);
    let fx = { rate: 1, date: null };
    if (!isRsd) {
      fx = await getRateForSave();
      if (!fx) {
        setSaving(false);
        setSaveError(tr(
          'Kurs NBS trenutno nije dostupan, pa faktura nije sačuvana. Pokušajte ponovo za minut.',
          'The NBS exchange rate is not available right now, so the invoice was not saved. Please try again in a minute.',
          'Курс НБС сейчас недоступен, счёт не сохранён. Попробуйте ещё раз через минуту.',
        ));
        return;
      }
    }
    const payload = {
      agency_id: activeAgencyId,
      customer_id: customer?.id || null,
      client: customer?.name || originalClient || '',
      document_kind: documentKind,
      type,
      invoice_number: invoiceNumber,
      items: cleanItems,
      description: cleanItems.map((it) => it.name).join('; '),
      amount: totals.total,
      currency: isRsd ? 'RSD' : currency,
      exchange_rate: isRsd ? 1 : fx.rate,
      doc_lang: isRsd ? 'sr' : docLang,
      issue_date: issueDate,
      service_date: serviceDate,
      place_of_issue: placeOfIssue,
      nbs_rate_date: isRsd ? null : (fx.date || null),
      due_date: documentKind !== 'fiskalni' && hasDue && dueDate ? dueDate : null,
      note: note.trim() || null,
      issuer_name_mode: p.short_name ? issuerNameMode : 'full',
    };
    // Status se postavlja samo pri izdavanju; izmena fakture ne menja status naplate
    if (!isEdit) {
      if (sourceProforma && documentKind === 'faktura') payload.source_proforma_id = sourceProforma.id;
      if (documentKind === 'profaktura') payload.status = 'u_pripremi';
      else if (documentKind === 'fiskalni') {
        payload.status = 'naplaceno';
        payload.paid_amount = totals.total;
        payload.paid_at = issueDate;
      } else payload.status = 'poslato';
    }
    const { data, error } = isEdit
      ? await supabase.from('invoices').update(payload).eq('id', id).select().single()
      : await supabase.from('invoices').insert(payload).select().single();
    setSaving(false);
    if (error) {
      // Baza dozvoljava samo jednu (nestorniranu) fakturu od iste profakture
      if (error.code === '23505' && /invoices_one_active_invoice_per_proforma/.test(error.message || '')) {
        setSaveError(tr(
          'Od ove profakture je već napravljena faktura. Ako želite novu, prvo stornirajte postojeću.',
          'An invoice has already been created from this proforma. To create a new one, cancel the existing invoice first.',
          'По этой проформе уже создан счёт. Чтобы создать новый, сначала аннулируйте существующий.',
        ));
        return;
      }
      // Baza ne dozvoljava isti broj iste vrste dokumenta dva puta u istoj firmi
      if (error.code === '23505' && /invoices_unique_number_per_agency/.test(error.message || '')) {
        setSaveError(tr(
          `Broj ${invoiceNumber} već postoji. Izaberite drugi broj.`,
          `Number ${invoiceNumber} already exists. Please choose another number.`,
          `Номер ${invoiceNumber} уже существует. Выберите другой номер.`,
        ));
        return;
      }
      setSaveError(error.message + (error.details ? ' — ' + error.details : '') + (error.hint ? ' (' + error.hint + ')' : ''));
      return;
    }
    if (data) {
      if (refreshYears) refreshYears();
      navigate(`/fakturisanje/${data.id}/pregled`, { replace: isEdit });
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-500)' }}>{t('loadingGeneric')}</div>;

  const kindLabel = DOC_KINDS.find((k) => k.key === documentKind)?.label || t('docKindInvoice');
  const SAVE_LABELS = {
    faktura: tr('Sačuvaj fakturu', 'Save invoice', 'Сохранить счёт'),
    profaktura: tr('Sačuvaj profakturu', 'Save proforma', 'Сохранить проформу'),
    fiskalni: tr('Sačuvaj fiskalni račun', 'Save fiscal receipt', 'Сохранить фискальный чек'),
  };
  const saveLabel = saving ? t('savingEllipsisShort') : (isEdit ? t('saveChanges') : (SAVE_LABELS[documentKind] || `${tr('Sačuvaj')} ${kindLabel.toLowerCase()}`));

  return (
    <div style={styles.page} className="inv-form">
      {/* Gornja traka: nazad + čuvanje */}
      <div style={styles.bar}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>
          <Icon name="arrowLeft" size={16} />
          {isEdit ? t('editDocumentHeading') : t('newDocumentHeading')}
        </button>
        <button onClick={handleSave} disabled={!valid || saving} style={{ ...styles.primaryBtn, opacity: valid && !saving ? 1 : 0.5 }}>
          <Icon name="check" size={16} strokeWidth={2.2} />
          {saveLabel}
        </button>
      </div>

      {sourceProforma && (
        <div style={alreadyInvoiced ? styles.errorBox : styles.fromProforma}>
          {alreadyInvoiced ? (
            <>
              {tr(`Od profakture ${sourceProforma.number || ''} je već napravljena faktura `, `An invoice has already been created from proforma ${sourceProforma.number || ''}: `, `По проформе ${sourceProforma.number || ''} уже создан счёт `)}
              <button type="button" onClick={() => navigate(`/fakturisanje/${alreadyInvoiced.id}/pregled`)} style={styles.inlineLink}>{alreadyInvoiced.invoice_number || tr('otvori', 'open', 'открыть')}</button>.
              {' '}{tr('Za novu fakturu prvo stornirajte postojeću.', 'To create a new one, cancel it first.', 'Чтобы создать новый, сначала аннулируйте его.')}
            </>
          ) : (
            tr(`Faktura od profakture ${sourceProforma.number || ''}. Proverite datum, broj i stavke pa sačuvajte.`, `Invoice from proforma ${sourceProforma.number || ''}. Check the date, number and items, then save.`, `Счёт по проформе ${sourceProforma.number || ''}. Проверьте дату, номер и позиции и сохраните.`)
          )}
        </div>
      )}

      {!forceKind && !sourceProforma && (
        <div style={styles.kindTabs}>
          {DOC_KINDS.map((k) => (
            <button key={k.key} onClick={() => setDocumentKind(k.key)} style={{ ...styles.kindTab, ...(documentKind === k.key ? styles.kindTabActive : {}) }}>
              <Icon name={k.icon} size={17} />
              {k.label}
            </button>
          ))}
        </div>
      )}

      <div style={styles.doc} className="inv-form-doc">
        {/* ---------- Zaglavlje ---------- */}
        <div style={styles.row}>
          <div style={styles.colLeft}>
            {p.logo_data && <img src={p.logo_data} alt="" style={styles.logo} />}
            <div style={styles.issuerName}>{issuerName || '—'}</div>
            {p.pib && <div style={styles.muted}>PIB: {p.pib}</div>}
            {p.mb && <div style={styles.muted}>Matični broj: {p.mb}</div>}
            {issuerAddress && <div style={styles.muted}>{issuerAddress}</div>}
          </div>
          <div style={styles.colRight}>
            <label style={styles.label}>{kindLabel}</label>
            <input
              value={invoiceNumber}
              onChange={(e) => { setInvoiceNumber(e.target.value); setNumberTouched(true); }}
              placeholder={t('invoiceNumberPlaceholder')}
              style={{ ...styles.input, fontWeight: 700 }}
            />
            {!isEdit && !numberTouched && invoiceNumber && <div style={styles.hint}>{t('numberSuggestedNote')}</div>}
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>{tr('Datum fakture')}</label>
                <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} style={styles.input} />
              </div>
              {documentKind !== 'fiskalni' ? (
                <div>
                  <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={hasDue} onChange={(e) => setHasDue(e.target.checked)} style={styles.checkbox} />
                    {tr('Datum valute')}
                  </label>
                  {hasDue
                    ? <input type="date" value={dueDate} onChange={(e) => { setDueDate(e.target.value); setDueTouched(true); }} style={styles.input} />
                    : <div style={{ ...styles.input, ...styles.inputOff }}>{tr('Ne prikazuje se')}</div>}
                </div>
              ) : <div />}
              <div>
                <label style={styles.label}>{tr('Datum prometa')}</label>
                <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>{tr('Mesto prometa')}</label>
                <input value={placeOfIssue} onChange={(e) => setPlaceOfIssue(e.target.value)} placeholder={t('placeOfIssuePlaceholder')} style={styles.input} />
              </div>
            </div>
          </div>
        </div>

        <div style={styles.divider} />

        {/* ---------- Klijent i "Od" ---------- */}
        <div style={styles.row}>
          <div style={styles.colLeft}>
            <label style={styles.label}>{tr('Klijent')}</label>
            <ClientPicker value={customer} onChange={setCustomer} />

            {documentKind !== 'fiskalni' && (
              <>
                <label style={{ ...styles.label, marginTop: 14 }}>{t('invoiceTypeLabel')}</label>
                <div style={styles.typeTabs}>
                  {['domaca', 'inostrana'].map((tp) => (
                    <button key={tp} onClick={() => setType(tp)} style={{ ...styles.typeTab, ...(type === tp ? styles.typeTabActive : {}) }}>
                      <Icon name={tp === 'domaca' ? 'mapPin' : 'globe'} size={16} />
                      {tp === 'domaca' ? t('domesticTab') : t('foreignTab')}
                    </button>
                  ))}
                </div>
              </>
            )}

            {!isRsd && (
              <div style={styles.fxBox}>
                <label style={styles.label}>{t('currencyLabel')}</label>
                <select value={currency} onChange={(e) => { setCurrency(e.target.value); setExchangeRate(''); setNbsDate(''); }} style={styles.input}>
                  {POPULAR_CURRENCIES.map(([code, sr, en, ru]) => (
                    <option key={code} value={code}>{code} — {tr(sr, en, ru)}</option>
                  ))}
                  {!POPULAR_CURRENCIES.some(([code]) => code === currency) && <option value={currency}>{currency}</option>}
                </select>
                <label style={styles.label}>{tr('Jezik fakture', 'Invoice language', 'Язык счёта')}</label>
                <select value={docLang} onChange={(e) => setDocLang(e.target.value)} style={styles.input}>
                  <option value="sr">Srpski</option>
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                </select>
                {docLang !== 'sr' && (
                  <div style={styles.hint}>
                    {tr(
                      'Svi tekstovi na fakturi biće na izabranom jeziku. Naziv usluge i poruku napišite na tom jeziku.',
                      'All invoice texts will be in the selected language. Write the item names and message in that language.',
                      'Все тексты счёта будут на выбранном языке. Названия услуг и сообщение напишите на этом языке.',
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.colRight}>
            <label style={styles.label}>{tr('Od')}</label>
            {p.short_name ? (
              <select value={issuerNameMode} onChange={(e) => setIssuerNameMode(e.target.value)} style={styles.input}>
                <option value="full">{p.company_name} {tr('(pun naziv)')}</option>
                <option value="short">{p.short_name} {tr('(skraćeni naziv)')}</option>
              </select>
            ) : (
              <div style={{ ...styles.input, background: 'var(--paper)' }}>{p.company_name || '—'}</div>
            )}
            <div style={styles.fromInfo}>
              {issuerAddress && <div>{tr('Adresa:')} {issuerAddress}</div>}
              {isRsd
                ? <div>{tr('Tekući račun:')} <strong style={styles.mono}>{p.account_dom || '—'}</strong></div>
                : <>
                    <div>IBAN: <strong style={styles.mono}>{p.account_ino || '—'}</strong></div>
                    <div>SWIFT: <strong style={styles.mono}>{p.swift || '—'}</strong></div>
                  </>}
              {!account && (
                <button type="button" onClick={() => navigate('/agencija')} style={styles.linkBtn}>{tr('Dopuni račun na stranici Agencija →')}</button>
              )}
            </div>
          </div>
        </div>

        <div style={styles.divider} />

        {/* ---------- Stavke ---------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((it, i) => (
            <div key={i} style={styles.itemCard}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={styles.itemNo}>{i + 1}</span>
                <input
                  value={it.name}
                  onChange={setItem(i, 'name')}
                  placeholder={tr('Naziv usluge ili proizvoda')}
                  aria-label="Naziv stavke"
                  style={{ ...styles.input, marginBottom: 0, fontWeight: 600 }}
                />
                <button type="button" onClick={() => removeItem(i)} style={styles.trashBtn} title={tr('Ukloni stavku')} aria-label={tr('Ukloni stavku')}>
                  <Icon name="trash" size={16} />
                </button>
              </div>
              <div style={styles.itemGrid}>
                <div>
                  <label style={styles.label}>{tr('Jedinica mere')}</label>
                  <select
                    value={it.customUnit ? CUSTOM : (UNITS.includes(it.unit) ? it.unit : '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      setItems((prev) => prev.map((x, j) => (j === i
                        ? (v === CUSTOM ? { ...x, unit: '', customUnit: true } : { ...x, unit: v, customUnit: false })
                        : x)));
                    }}
                    style={styles.input}
                  >
                    <option value="">{tr('— prazno —', '— none —', '— пусто —')}</option>
                    <option value={CUSTOM}>{tr('Upiši sami…', 'Type your own…', 'Ввести свою…')}</option>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {it.customUnit && (
                    <input
                      value={it.unit}
                      onChange={setItem(i, 'unit')}
                      placeholder={tr('Upiši jedinicu', 'Type the unit', 'Введите единицу')}
                      aria-label="Sopstvena jedinica mere"
                      autoFocus={!it.unit}
                      style={{ ...styles.input, marginTop: -6 }}
                    />
                  )}
                </div>
                <div>
                  <label style={styles.label}>{tr('Količina')}</label>
                  <input type="number" step="any" min="0" value={it.quantity} onChange={setItem(i, 'quantity')} style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>{tr('Cena')} ({cur})</label>
                  <input type="number" step="any" min="0" value={it.price} onChange={setItem(i, 'price')} placeholder="0.00" style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>{tr('Popust %')}</label>
                  <input type="number" step="any" min="0" max="100" value={it.discount} onChange={setItem(i, 'discount')} style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>{tr('Ukupno')}</label>
                  <div style={{ ...styles.input, ...styles.lineTotal }}>{money(round2(lineTotal(it)))}</div>
                </div>
              </div>
            </div>
          ))}
          <div style={styles.addArea}>
            <button type="button" onClick={addItem} style={styles.primaryBtn}>
              <Icon name="plus" size={16} strokeWidth={2.2} />
              {tr('Dodaj uslugu ili proizvod')}
            </button>
          </div>
        </div>

        {/* ---------- Poruka i obračun ---------- */}
        <div style={{ ...styles.row, marginTop: 24, alignItems: 'flex-start' }}>
          <div style={styles.colLeft}>
            <label style={styles.label}>{tr('Poruka')}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              placeholder={tr('npr. Rok plaćanja 15 dana. Hvala na saradnji!')}
              style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit', minHeight: 120 }}
            />
          </div>
          <div style={styles.colRight}>
            <div style={styles.totalsBox} className="inv-form-totals">
              <div style={styles.totalRow}><span>{tr('Međuiznos:')}</span><strong>{money(totals.subtotal)}</strong></div>
              <div style={styles.totalRow}><span>{tr('Popust:')}</span><strong>{money(totals.discount)}</strong></div>
              <div style={{ ...styles.totalRow, ...styles.totalFinal }}><span>{tr('Ukupno:')}</span><strong>{money(totals.total)}</strong></div>
            </div>
            {saveError && <div style={styles.errorBox}>{saveError}</div>}
            {!valid && problems.length > 0 && <div style={styles.hintBox}>{tr('Za čuvanje:')} {problems.join(', ')}.</div>}
            <button onClick={handleSave} disabled={!valid || saving} style={{ ...styles.primaryBtn, width: '100%', justifyContent: 'center', minHeight: 48, opacity: valid && !saving ? 1 : 0.5 }}>
              <Icon name="check" size={16} strokeWidth={2.2} />
              {saveLabel}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Polja nikad ne izlaze iz svoje kolone (iPhone datumi su inače širi od kolone) */
        .inv-form input, .inv-form select, .inv-form textarea { min-width: 0; max-width: 100%; }
        .inv-form input[type="date"] { -webkit-appearance: none; appearance: none; display: flex; align-items: center; text-align: left; }
        .inv-form input[type="date"]::-webkit-date-and-time-value { text-align: left; margin: 0; }
        @media (max-width: 760px) {
          .inv-form-doc { padding: 18px 16px !important; border-radius: 16px !important; }
          .inv-form-totals { margin-top: 4px !important; }
          .inv-form input, .inv-form select, .inv-form textarea { font-size: 16px !important; }
        }
        /* Na telefonu i tabletu dugme asistenta se ne prikazuje dok se popunjava dokument */
        @media (max-width: 1023px) { .assistant-fab { display: none !important; } }
      `}</style>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1000, margin: '0 auto' },
  bar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 16px', marginBottom: 14 },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', color: 'var(--ink-900)', fontWeight: 700, fontSize: 15, padding: 0 },
  primaryBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
  kindTabs: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  fromProforma: { background: 'var(--teal-100)', color: 'var(--teal-800)', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 14 },
  inlineLink: { background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' },
  kindTab: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: '1 1 160px', minHeight: 44, border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, fontWeight: 600, fontSize: 14, color: 'var(--ink-700)' },
  kindTabActive: { background: 'var(--teal-800)', color: '#fff', borderColor: 'var(--teal-800)' },
  doc: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '28px 30px' },
  row: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  colLeft: { flex: '1 1 340px', minWidth: 0 },
  colRight: { flex: '1 1 340px', minWidth: 0 },
  logo: { maxHeight: 64, maxWidth: 220, objectFit: 'contain', display: 'block', marginBottom: 12 },
  issuerName: { fontSize: 16, fontWeight: 800, color: 'var(--ink-900)', marginBottom: 4 },
  muted: { fontSize: 14, color: 'var(--ink-700)', lineHeight: 1.7 },
  divider: { height: 1, background: 'var(--line)', margin: '26px 0' },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5, display: 'block', minHeight: 18, lineHeight: '18px' },
  checkbox: { width: 16, height: 16, margin: 0, flexShrink: 0, accentColor: 'var(--teal-800)' },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 14, width: '100%', marginBottom: 12, background: 'var(--surface)', minHeight: 44, boxSizing: 'border-box' },
  inputOff: { color: 'var(--ink-300)', background: 'var(--paper)', display: 'flex', alignItems: 'center' },
  hint: { fontSize: 12, color: 'var(--ink-500)', marginTop: -6, marginBottom: 12 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  typeTabs: { display: 'flex', gap: 8, marginBottom: 12 },
  typeTab: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: 1, minHeight: 42, border: '1.5px solid var(--line)', background: 'var(--surface)', borderRadius: 10, fontWeight: 600, fontSize: 13, color: 'var(--ink-700)' },
  typeTabActive: { background: 'var(--lavender-100)', color: 'var(--lavender-700)', borderColor: 'var(--lavender-700)' },
  fxBox: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: '12px 12px 2px' },
  nbsBtn: { display: 'flex', alignItems: 'center', gap: 4, minHeight: 44, marginBottom: 12, border: '1.5px solid var(--teal-800)', background: 'var(--surface)', color: 'var(--teal-800)', borderRadius: 10, padding: '0 10px', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' },
  fromInfo: { fontSize: 14, color: 'var(--ink-700)', lineHeight: 1.8 },
  mono: { fontFamily: 'monospace', fontWeight: 700 },
  linkBtn: { border: 'none', background: 'none', color: 'var(--teal-700)', fontWeight: 700, fontSize: 13, padding: 0, marginTop: 4 },
  itemCard: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 14px 2px' },
  itemNo: { width: 28, height: 28, borderRadius: 8, background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 },
  trashBtn: { width: 44, height: 44, flexShrink: 0, border: 'none', borderRadius: 10, background: 'var(--red-100)', color: 'var(--red-600)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 12 },
  lineTotal: { background: 'var(--surface)', fontWeight: 700, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden' },
  addArea: { border: '1.5px dashed var(--line)', borderRadius: 10, padding: 18, display: 'flex', justifyContent: 'center' },
  totalsBox: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginBottom: 12, marginTop: 23 },
  totalRow: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '4px 0', fontSize: 14, color: 'var(--ink-900)' },
  totalFinal: { borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 10, fontSize: 17 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12, wordBreak: 'break-word' },
  hintBox: { background: 'var(--gold-100)', color: 'var(--gold-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
};
